import {
  OverflowTooltip,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MainApi from "../../../../services/mainApi.service";
import MacroFilter from "./MacroFilter";
import {
  FilterParamEntry,
  FilterParamFromEntries,
  IFilterParams,
} from "./models";
import { ITicketCalendarExamAttachmentLog } from "../../../../models/ticket-calendar-exam-attachment-log";
import { restartTicketCalendarExamAttachmentLogRetries } from "../../../../services/ticketCalendarExamAttachmentLog.service";

const TODAY = new Date();

const FILTER_PARAMS_ITEMS = {
  dateStart: { value: TODAY, label: "date_start" },
  dateEnd: { value: TODAY, label: "date_end" },
  calendarExamIdentifier: { value: "", label: "identifier" },
  status: { value: undefined, label: "status" },
};

const TICKET_CALENDAR_EXAM_ATTACHMENT_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketCalendarExamAttachmentLog";

const ticketCalendarExamAttachmentLogService =
  new MainApi<ITicketCalendarExamAttachmentLog>(
    TICKET_CALENDAR_EXAM_ATTACHMENT_ROUTE,
  );

const translationPath = "chatBot.page.logs.examSchedulingAttachmentLog.";

const ExamSchedulingAttachmentLogs = () => {
  const { t } = useTranslation();

  const { setMessage } = useNotificationContext();

  const { sendRequest: restartTicketCalendarExamAttachmentLogRetriesRequest } =
    useFetch(restartTicketCalendarExamAttachmentLogRetries);

  const [refresh, setRefresh] = useState(0);

  const [filterParams, setFilterParams] =
    useState<IFilterParams>(FILTER_PARAMS_ITEMS);

  const columns: GridColDef<ITicketCalendarExamAttachmentLog>[] = [
    {
      field: "calendarExamIdentifier",
      headerName: t(translationPath + "identifier") || "Identifier",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";
        return <OverflowTooltip text={params.value} tooltip={params.value} />;
      },
    },
    {
      field: "ticketId",
      headerName: t(translationPath + "ticket_id") || "Ticket ID",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";
        return <OverflowTooltip text={params.value} tooltip={params.value} />;
      },
    },
    {
      field: "protocolNumber",
      headerName: t(translationPath + "protocol_number") || "Protocol Number",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";
        return <OverflowTooltip text={params.value} tooltip={params.value} />;
      },
    },
    {
      field: "ticketSequential",
      headerName: t(translationPath + "ticket_number") || "Ticket Number",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";
        return <OverflowTooltip text={params.value} tooltip={params.value} />;
      },
    },
    {
      field: "integrationId",
      headerName: t(translationPath + "integration_id") || "Integration ID",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";
        return <OverflowTooltip text={params.value} tooltip={params.value} />;
      },
    },
    {
      field: "errorMessage",
      headerName: t(translationPath + "error_message") || "Error Message",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";
        return <OverflowTooltip text={params.value} tooltip={params.value} />;
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Retries",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (typeof params.value !== "number" && !params.value) return "-";
        return <OverflowTooltip text={params.value} tooltip={params.value} />;
      },
    },
  ];

  const params = useMemo(() => {
    const filterParamsArray = Object.entries(
      filterParams,
    ) as FilterParamEntry[];
    const filterParamsFormatted = Object.fromEntries(
      filterParamsArray.map(([key, value]) => [key, value?.value]),
    ) as FilterParamFromEntries;

    return {
      refresh,
      ...filterParamsFormatted,
      dateStart: filterParamsFormatted.dateStart
        ? new Date(filterParamsFormatted.dateStart.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
      dateEnd: filterParamsFormatted.dateEnd
        ? new Date(filterParamsFormatted.dateEnd.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
      status: filterParamsFormatted.status,
    };
  }, [refresh, filterParams]);

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      onRefreshHandler();
    },
    [onRefreshHandler],
  );

  const handleRetry = useCallback(
    async (id: string) => {
      const { success } =
        await restartTicketCalendarExamAttachmentLogRetriesRequest({
          id,
        });

      if (success) {
        onRefreshHandler();
        setMessage({
          message: t(translationPath + "reseted_success"),
          type: "success",
        });
      }
    },
    [
      onRefreshHandler,
      restartTicketCalendarExamAttachmentLogRetriesRequest,
      setMessage,
      t,
    ],
  );

  return (
    <Table<ITicketCalendarExamAttachmentLog>
      title={t(translationPath + "title")}
      columns={columns}
      service={ticketCalendarExamAttachmentLogService}
      searchInputPlaceHolder={
        t(translationPath + "search_placeholder") || "Search..."
      }
      showDefaultMacroFilters={false}
      showButton={false}
      showDeleteButton={false}
      defaultActions={true}
      getRowId={(row) => (row.id ? row.id : 0)}
      rowActions={(params) => {
        const actions = [];
        if (params.row.retries === 3) {
          actions.push({
            action: () => {
              handleRetry(params.row.id);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "force_retry") || "Force Retry",
          });
        }
        return actions;
      }}
      serviceProps={params}
      macroFilters={useCallback(
        () => (
          <MacroFilter
            filterParams={filterParams}
            onFilterParams={handleFilterChange}
          />
        ),
        [filterParams, handleFilterChange],
      )}
    />
  );
};

export default ExamSchedulingAttachmentLogs;
